import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Coins, X, ChevronRight } from 'lucide-react';
import { useCauldronTokenList } from '../hooks/useCauldronToken';
import { formatPricePerToken, type CauldronToken } from '../lib/cauldron';

const DISMISSED_KEY = 'bchnostr:trending-tokens-card-dismissed';

/**
 * Compact, dismissible discovery card showing the most-traded BCH CashTokens
 * on Cauldron. Sits in the feed's discovery zone. Each chip links to the
 * token's page. Read-only — data from the Cauldron indexer.
 */
export default function TrendingTokensCard() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const { tokens, loading } = useCauldronTokenList('volume');

  if (dismissed) return null;
  // Nothing to show (indexer down / empty) → render nothing rather than an
  // empty shell.
  if (!loading && tokens.length === 0) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
  };

  const top = tokens.slice(0, 10);

  return (
    <div
      className="mx-2 sm:mx-0 mb-3 rounded-xl border border-primary/20 bg-primary/5 overflow-hidden"
      style={{ contain: 'content' }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <TrendingUp className="w-4 h-4 text-primary shrink-0" />
        <p className="text-sm font-semibold text-foreground flex-1">Trending tokens</p>
        <Link to="/tokens" className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-0.5">
          See all <ChevronRight className="w-3.5 h-3.5" />
        </Link>
        <button
          onClick={handleDismiss}
          className="p-1 rounded-lg hover:bg-muted transition-colors shrink-0"
          title="Dismiss"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Horizontal scroll strip */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-3 pt-0.5">
        {loading && top.length === 0
          ? Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="shrink-0 w-32 h-[72px] rounded-lg bg-muted/50 animate-pulse" />
            ))
          : top.map(token => <TrendingChip key={token.tokenId} token={token} />)}
      </div>
    </div>
  );
}

function TrendingChip({ token }: { token: CauldronToken }) {
  const [iconFailed, setIconFailed] = useState(false);
  const up = token.change24h >= 0;

  return (
    <Link
      to={`/token/${token.tokenId}`}
      className="shrink-0 w-32 rounded-lg border border-border bg-background p-2.5 hover:border-primary/40 transition-colors no-underline"
    >
      <div className="flex items-center gap-1.5">
        {iconFailed || !token.iconUrl ? (
          <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Coins className="w-3 h-3" />
          </div>
        ) : (
          <img
            src={token.iconUrl}
            alt={token.symbol}
            width={20}
            height={20}
            loading="lazy"
            decoding="async"
            className="w-5 h-5 rounded-full bg-muted object-cover shrink-0"
            onError={() => setIconFailed(true)}
          />
        )}
        <span className="font-bold text-xs truncate">${token.symbol}</span>
      </div>
      <p className="text-xs font-semibold mt-1.5 truncate">{formatPricePerToken(token.priceUsd)}</p>
      {token.change24h !== 0 && (
        <p className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${up ? 'text-emerald-500' : 'text-red-500'}`}>
          {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
          {up ? '+' : ''}{token.change24h.toFixed(1)}%
        </p>
      )}
    </Link>
  );
}
